import { useCallback, useEffect, useRef } from 'react'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { flexRender } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'

import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'

export function DraggableHeader({ header, sort, setSort, setColSorting }) {
	const { attributes, isDragging, listeners, setNodeRef, transform } =
		useSortable({
			id: header.column.id,
		})

	const isSelect = header.column.id === 'select'
	const canSort = header.column.getCanSort()
	const sortable = setSort && canSort
	const isSorted = sortable && sort?.replace('-', '') === header.column.id
	const isDesc = isSorted && sort.startsWith('-')
	const isResizing = header.column.getIsResizing()

	// Use refs to avoid re-renders when managing cursor
	const prevCursorRef = useRef(null)

	useEffect(() => {
		const root = document.documentElement
		let cursor = null

		if (isResizing) {
			cursor = 'ew-resize'
		} else if (isDragging) {
			cursor = 'grabbing'
		}

		// Only update if cursor actually changed
		if (cursor !== prevCursorRef.current) {
			if (cursor) {
				root.style.setProperty('cursor', cursor, 'important')
			} else {
				root.style.removeProperty('cursor')
			}
			prevCursorRef.current = cursor
		}

		// Cleanup on unmount
		return () => {
			if (prevCursorRef.current) {
				root.style.removeProperty('cursor')
			}
		}
	}, [isDragging, isResizing])

	const _setSort = useCallback(() => {
		if (!sortable) return

		const newSortValue =
			isSorted && isDesc
				? undefined
				: isSorted && !isDesc
					? `-${header.column.id}`
					: header.column.id

		setSort(newSortValue)

		setColSorting(
			newSortValue
				? [
						{
							id: header.column.id,
							desc: newSortValue === `-${header.column.id}`,
						},
					]
				: []
		)
	}, [setSort, header.column.id, isDesc, sortable, isSorted, setColSorting])

	const width = !isNaN(header.getSize()) ? header.getSize() : 'auto'

	return (
		<td
			ref={setNodeRef}
			colSpan={header.colSpan}
			className={`
        relative overflow-hidden whitespace-nowrap min-h-0
        ${header.index === 0 ? '' : 'border-l'} border-gray-200 dark:border-gray-700
        bg-white dark:bg-gray-800
        text-gray-900 dark:text-gray-100
        uppercase font-semibold leading-6
        ${isDragging ? 'opacity-80 z-10' : 'z-0'}
        ${isResizing ? 'cursor-ew-resize' : 'cursor-grab'}
      `}
			style={{
				width,
				transform: CSS.Translate.toString(transform),
				transition:
					!isDragging && !isResizing
						? 'width 200ms ease-in-out'
						: 'none',
			}}
		>
			<div className="h-full flex flex-row">
				{isSelect ? (
					flexRender(
						header.column.columnDef.header,
						header.getContext()
					)
				) : (
					<div
						className={`
              w-full h-full flex items-center pl-1.5
              bg-white dark:bg-gray-800
              text-gray-900 dark:text-gray-100
              ${isResizing ? 'cursor-inherit' : isDragging ? 'cursor-grabbing' : 'cursor-grab'}
            `}
					>
						{!header.isPlaceholder && (
							<div className="flex flex-row justify-between flex-1">
								<div
									{...attributes}
									{...listeners}
									className="flex-1"
								>
									{flexRender(
										header.column.columnDef.header,
										header.getContext()
									)}
								</div>
								<div className="mr-2">
									{isSorted &&
										(isDesc ? (
											<Tooltip>
												<TooltipTrigger>
													<ArrowDown
														className="w-3.5 h-3.5 hover:cursor-pointer"
														onClick={_setSort}
													/>
												</TooltipTrigger>
												<TooltipContent>
													<p>Remove sort</p>
												</TooltipContent>
											</Tooltip>
										) : (
											<Tooltip>
												<TooltipTrigger>
													<ArrowUp
														className="w-3.5 h-3.5 hover:cursor-pointer"
														onClick={_setSort}
													/>
												</TooltipTrigger>
												<TooltipContent>
													<p>Sort descending</p>
												</TooltipContent>
											</Tooltip>
										))}
									{sortable && !isSorted && (
										<Tooltip>
											<TooltipTrigger>
												<ArrowUpDown
													className="w-3.5 h-3.5 hover:cursor-pointer"
													onClick={_setSort}
												/>
											</TooltipTrigger>
											<TooltipContent>
												<p>Sort</p>
											</TooltipContent>
										</Tooltip>
									)}
								</div>
							</div>
						)}
					</div>
				)}
			</div>
			<div
				className={`
          absolute right-0 top-0 h-full w-1
          opacity-0 hover:opacity-100
          bg-gray-400 dark:bg-gray-600
          cursor-ew-resize select-none touch-none
          ${isResizing ? 'opacity-100 bg-gray-500 dark:bg-gray-500' : ''}
        `}
				onMouseDown={header.getResizeHandler()}
				onTouchStart={header.getResizeHandler()}
			/>
		</td>
	)
}
